import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, ArrowLeft } from 'lucide-react';
import { Signature } from '../types/jobcard';
import { getJobCardSignatures } from '../services/jobcardApi';
import CreateSignatureModal from '../components/modals/CreateSignatureModal';
import { getSignatureImageUrl } from '../config';

export default function JobCardSignaturesPage() {
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const [signatures, setSignatures] = useState<Signature[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const fetchSignatures = async () => {
        try {
            const response = await getJobCardSignatures(Number(id));
            setSignatures(response.data.data);
        } catch (err) {
            setError('Failed to fetch signatures');
            console.error(err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchSignatures();
    }, [id]);

    const handleCreateSuccess = () => {
        setIsCreateModalOpen(false);
        fetchSignatures();
    };

    if (isLoading) return <div className="text-center py-8">Loading...</div>;
    if (error) return <div className="text-red-600">{error}</div>;

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/jobcards')}
                        className="p-2 text-gray-600 hover:text-gray-900"
                    >
                        <ArrowLeft size={24} />
                    </button>
                    <h1 className="text-2xl font-bold">Signatures</h1>
                </div>
                <button
                    onClick={() => setIsCreateModalOpen(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                    <Plus size={20} />
                    <span>Add Signature</span>
                </button>
            </div>

            <div className="grid gap-4">
                {signatures.map((signature) => (
                    <div
                        key={signature.id}
                        className="p-4 bg-white rounded-lg shadow"
                    >
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="font-medium text-lg">{signature.sign_name}</h3>
                                <span className={`inline-block px-2 py-1 rounded-full text-sm ${signature.is_manager
                                    ? 'bg-purple-100 text-purple-800'
                                    : 'bg-blue-100 text-blue-800'
                                    }`}>
                                    {signature.is_manager == true ? 'Manager' : 'Technician'}
                                </span>
                            </div>
                        </div>
                        {signature.picture && (
                            <div className="mt-4">
                                <img
                                    src={getSignatureImageUrl(signature.picture)}
                                    alt="Signature"
                                    className="max-w-full h-auto"
                                    onError={(e) => {
                                        console.error(`Failed to load signature: ${signature.picture}`, e);
                                    }}
                                />
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <CreateSignatureModal
                jobCardId={Number(id)}
                isOpen={isCreateModalOpen}
                onClose={() => setIsCreateModalOpen(false)}
                onSuccess={handleCreateSuccess}
            />
        </div>
    );
} 